import React from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import {
  Users,
  Search,
  Radar,
  Gavel,
  Activity,
  ShieldAlert,
  Calculator,
  FileText,
  CreditCard,
  Settings,
} from "lucide-react";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TagsProvider } from "@/context/TagsContext";
import ProtectedRoute from "@/routes/ProtectedRoute";
import PublicOnlyRoute from "@/routes/PublicOnlyRoute";
import AuthShell from "@/layouts/AuthShell";

import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyContactPage from "@/pages/auth/VerifyContactPage";
import TwoFactorPage from "@/pages/auth/TwoFactorPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import InviteAcceptPage from "@/pages/auth/InviteAcceptPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import OnboardingRoot from "@/pages/onboarding/OnboardingRoot";
import MyDebtorsPage from "@/pages/debtors/MyDebtorsPage";
import DebtorProfilePage from "@/pages/debtors/DebtorProfilePage";
import AddDebtorPage from "@/pages/debtors/AddDebtorPage";
import ActivityPage from "@/pages/ActivityPage";
import QueryResultsPage from "@/pages/queries/QueryResultsPage";
import ReportsPage from "@/pages/reports/ReportsPage";
import ReportViewPage from "@/pages/reports/ReportViewPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import TwoFactorSetupPage from "@/pages/account/TwoFactorSetupPage";
import AccountRecoverPage from "@/pages/account/AccountRecoverPage";
import BankruptcyWatchPage from "@/pages/bankruptcy/BankruptcyWatchPage";
import BillingPage from "@/pages/billing/BillingPage";
import RunQueryPage from "@/pages/queries/RunQueryPage";
import InterestCalculatorPage from "@/pages/interest/InterestCalculatorPage";
import MonitoringPage from "@/pages/monitoring/MonitoringPage";
import DemoShell from "@/pages/demo/DemoShell";
import DemoLandingPage from "@/pages/demo/DemoLandingPage";
import DemoDashboardPage from "@/pages/demo/DemoDashboardPage";
import DemoDebtorsPage from "@/pages/demo/DemoDebtorsPage";
import DemoDebtorProfilePage from "@/pages/demo/DemoDebtorProfilePage";
import DemoBankruptcyPage from "@/pages/demo/DemoBankruptcyPage";
import DemoReportsPage from "@/pages/demo/DemoReportsPage";
import DemoRunQueryPage from "@/pages/demo/DemoRunQueryPage";
import DemoDiscoveryPage from "@/pages/demo/DemoDiscoveryPage";
import DemoEnforcementPage from "@/pages/demo/DemoEnforcementPage";
import DemoMonitoringPage from "@/pages/demo/DemoMonitoringPage";
import DemoInterestCalculatorPage from "@/pages/demo/DemoInterestCalculatorPage";
import DemoBillingPage from "@/pages/demo/DemoBillingPage";
import DemoSettingsPage from "@/pages/demo/DemoSettingsPage";
import LandingPage from "@/pages/marketing/LandingPage";
import DiscoveryPage from "@/pages/discovery/DiscoveryPage";
import EnforcementPage from "@/pages/enforcement/EnforcementPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminTwoFactorPage from "@/pages/admin/AdminTwoFactorPage";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminBrokersPage from "@/pages/admin/AdminBrokersPage";
import AdminRenewalRulesPage from "@/pages/admin/AdminRenewalRulesPage";
import AdminSubscriptionsPage from "@/pages/admin/AdminSubscriptionsPage";
import AdminAlertsPage from "@/pages/admin/AdminAlertsPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminShell from "@/layouts/AdminShell";
import AdminProtectedRoute from "@/routes/AdminProtectedRoute";
import SessionExpiryModal from "@/components/jea/SessionExpiryModal";

const RootRedirect = () => {
  const { isAuthenticated, isAdmin, booting, user } = useAuth();
  if (booting) return null;
  if (isAuthenticated) {
    if (isAdmin) return <Navigate to="/admin" replace />;
    if (user && user.onboarding_completed === false && !user.onboarding_deferred) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <TagsProvider>
          <BrowserRouter>
          <Toaster
            position="top-right"
            theme="dark"
            richColors
            toastOptions={{
              style: {
                background: "#0D1E36",
                color: "#FFFFFF",
                border: "1px solid #1E3A58",
              },
            }}
          />
          <Routes>
            {/* Public */}
            <Route path="/demo" element={<DemoShell />}>
              <Route index element={<DemoLandingPage />} />
              <Route path="dashboard" element={<DemoDashboardPage />} />
              <Route path="debtors" element={<DemoDebtorsPage />} />
              <Route path="debtors/:id" element={<DemoDebtorProfilePage />} />
              <Route path="run-query" element={<DemoRunQueryPage />} />
              <Route path="discovery" element={<DemoDiscoveryPage />} />
              <Route path="enforcement" element={<DemoEnforcementPage />} />
              <Route path="monitoring" element={<DemoMonitoringPage />} />
              <Route path="bankruptcy-watch" element={<DemoBankruptcyPage />} />
              <Route path="interest-calculator" element={<DemoInterestCalculatorPage />} />
              <Route path="reports" element={<DemoReportsPage />} />
              <Route path="billing" element={<DemoBillingPage />} />
              <Route path="settings" element={<DemoSettingsPage />} />
            </Route>

            {/* Public */}
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <LoginPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicOnlyRoute>
                  <RegisterPage />
                </PublicOnlyRoute>
              }
            />
            <Route path="/2fa" element={<TwoFactorPage />} />
            <Route path="/verify-contact" element={<VerifyContactPage />} />
            <Route
              path="/setup-2fa"
              element={
                <ProtectedRoute>
                  <TwoFactorSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicOnlyRoute>
                  <ForgotPasswordPage />
                </PublicOnlyRoute>
              }
            />

            {/* Public — team invite acceptance (auth-agnostic; uses signed token). */}
            <Route path="/invite/accept" element={<InviteAcceptPage />} />
            <Route path="/account/recover" element={<AccountRecoverPage />} />

            {/* Onboarding (authenticated, but OUTSIDE shell) */}
            <Route path="/onboarding/*" element={<OnboardingRoot />} />
            <Route path="/onboarding" element={<OnboardingRoot />} />

            {/* Root — public marketing landing (authed users redirected inside RootRedirect) */}
            <Route path="/" element={<RootRedirect />} />

            {/* Protected shell */}
            <Route
              element={
                <ProtectedRoute>
                  <AuthShell />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/debtors" element={<MyDebtorsPage />} />
              <Route path="/debtors/new" element={<><MyDebtorsPage /><AddDebtorPage /></>} />
              <Route path="/debtors/:id" element={<DebtorProfilePage />} />
              <Route path="/debtors/:id/queries/:run_id" element={<QueryResultsPage />} />
              <Route path="/debtors/:id/reports/:report_id" element={<ReportViewPage />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route
                path="/run-query"
                element={<RunQueryPage />}
              />
              <Route
                path="/discovery"
                element={<DiscoveryPage />}
              />
              <Route
                path="/enforcement"
                element={<EnforcementPage />}
              />
              <Route
                path="/monitoring"
                element={<MonitoringPage />}
              />
              <Route
                path="/bankruptcy"
                element={<BankruptcyWatchPage />}
              />
              <Route
                path="/interest"
                element={<InterestCalculatorPage />}
              />
              <Route
                path="/interest-calculator"
                element={<InterestCalculatorPage />}
              />
              <Route
                path="/reports"
                element={<ReportsPage />}
              />
              <Route
                path="/billing"
                element={<BillingPage />}
              />
              <Route
                path="/settings"
                element={<SettingsPage />}
              />
              <Route
                path="/account/2fa"
                element={<TwoFactorSetupPage />}
              />
            </Route>

            {/* Fallback */}

            {/* Admin auth (public) */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/2fa" element={<AdminTwoFactorPage />} />

            {/* Admin protected shell */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminShell />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<AdminOverviewPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="brokers" element={<AdminBrokersPage />} />
              <Route path="renewal-rules" element={<AdminRenewalRulesPage />} />
              <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
              <Route path="alerts" element={<AdminAlertsPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SessionExpiryModal />
          </BrowserRouter>
        </TagsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
